import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Form,
    Input,
    Button,
    Card,
    Row,
    Col,
    Select,
    Upload,
    message,
    Divider,
    Switch,
    Tag,
    InputNumber,
} from "antd";
import {
    PlusOutlined,
    MinusCircleOutlined,
    UploadOutlined,
    SaveOutlined,
    ArrowLeftOutlined,
} from "@ant-design/icons";
import http from "../../helpers/http";
import HtmlEditor from "../../components/HtmlEditor";

const { TextArea } = Input;

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/";
const base_url = `${API_URL}admin/products`;
const image_url = `${API_URL}uploads/images/`;

function ProductCreate() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const selectedCategory = searchParams.get("category") || "";
    const isBespoke = selectedCategory.toLowerCase() === "bespoke";

    const [form] = Form.useForm();
    const [submitLoading, setSubmitLoading] = useState(false);

    const [bannerImage, setBannerImage] = useState([]);
    const [bannerImageMobile, setBannerImageMobile] = useState([]);
    const [heroImage, setHeroImage] = useState([]);
    const [gridImages, setGridImages] = useState([]);       // [{ file, order }]
    const [galleryImages, setGalleryImages] = useState([]); // [{ file, order }]
    const [resources, setResources] = useState([]);

    const [keywords, setKeywords] = useState([]);
    const [sizes, setSizes] = useState([]);
    const [availability, setAvailability] = useState([]);
    const [areasOfUse, setAreasOfUse] = useState([]);
    const [siblingStockedSizes, setSiblingStockedSizes] = useState([]);
    const [colors, setColors] = useState([]);
    const [properties, setProperties] = useState({});
    const [propertyKey, setPropertyKey] = useState("");
    const [propertyValue, setPropertyValue] = useState("");
    const [description, setDescription] = useState("");

    // Accordion state: [{ key, value }]
    const [accordion, setAccordion] = useState([]);
    const [accordionKey, setAccordionKey] = useState("");
    const [accordionValue, setAccordionValue] = useState("");

    const [linkedProductsList, setLinkedProductsList] = useState([]);

    useEffect(() => {
        if (!selectedCategory) {
            navigate("/products");
            return;
        }
        form.setFieldsValue({
            category: selectedCategory,
            availability,
            sizes,
            areasOfUse,
            bannerImage,
            bannerImageMobile,
            colors,
            resources,
            properties,
            siblingStockedSizes
        });
        fetchLinkedProducts(selectedCategory);
    }, [form, navigate, selectedCategory, availability, sizes, areasOfUse, bannerImage, bannerImageMobile, colors, resources, properties, siblingStockedSizes]);

    const fetchLinkedProducts = async (currentCategory) => {
        if (!currentCategory) return;
        const lowerCat = currentCategory.toLowerCase();
        const targetCategory = (lowerCat === "rug" || lowerCat === "rugs") ? "carpets" : "rugs";
        try {
            const response = await http.get(`${API_URL}admin/products?category=${targetCategory}&limit=1000`);
            if (response && response.products) {
                setLinkedProductsList(response.products);
            }
        } catch (error) {
            console.error("Error fetching linked products:", error);
        }
    };

    // ── Grid / Gallery helpers ──────────────────────────────────────────────

    const handleGridImageAdd = (file) => {
        setGridImages((prev) => {
            if (prev.length >= 2) return prev;
            const nextOrder = prev.length + 1;
            return [...prev, { uid: file.uid, file, order: nextOrder }];
        });
        return false; // prevent auto-upload
    };

    const handleGridImageRemove = (uid) =>
        setGridImages((prev) => prev.filter((item) => item.uid !== uid));

    const handleGridOrder = (uid, val) =>
        setGridImages((prev) =>
            prev.map((item) => (item.uid === uid ? { ...item, order: val } : item))
        );

    const handleGalleryImageAdd = (file) => {
        setGalleryImages((prev) => {
            if (prev.length >= 12) return prev;
            const nextOrder = prev.length + 1;
            return [...prev, { uid: file.uid, file, order: nextOrder }];
        });
        return false;
    };

    const handleGalleryImageRemove = (uid) =>
        setGalleryImages((prev) => prev.filter((item) => item.uid !== uid));

    const handleGalleryOrder = (uid, val) =>
        setGalleryImages((prev) =>
            prev.map((item) => (item.uid === uid ? { ...item, order: val } : item))
        );

    // ── Accordion helpers ───────────────────────────────────────────────────

    const handleAddAccordion = () => {
        if (accordionKey.trim() && accordionValue.trim()) {
            setAccordion((p) => [...p, { key: accordionKey.trim(), value: accordionValue.trim() }]);
            setAccordionKey("");
            setAccordionValue("");
        }
    };

    const handleRemoveAccordion = (idx) =>
        setAccordion((p) => p.filter((_, i) => i !== idx));

    const handleUpdateAccordionKey = (idx, val) =>
        setAccordion((p) => p.map((item, i) => (i === idx ? { ...item, key: val } : item)));

    const handleUpdateAccordionValue = (idx, val) =>
        setAccordion((p) => p.map((item, i) => (i === idx ? { ...item, value: val } : item)));

    // ── Submit ──────────────────────────────────────────────────────────────

    const handleSubmit = async () => {
        try {
            const formValues = await form.validateFields();

            if (!isBespoke && resources.length === 0) {
                message.error("Catalog PDF is required");
                return;
            }

            setSubmitLoading(true);
            const formData = new FormData();

            formData.append("name", formValues.name.trim());
            formData.append("category", formValues.category.trim());
            formData.append("description", description || "");
            formData.append("slug", formValues.slug || "");
            formData.append("gridTitle", formValues.gridTitle || "");
            formData.append("gridDescription", formValues.gridDescription || "");
            formData.append("featured", formValues.featured ? "true" : "false");
            formData.append("status", formValues.status || "active");
            if (formValues.linkedProduct) formData.append("linkedProduct", formValues.linkedProduct);

            formData.append("keywords", JSON.stringify(keywords));
            formData.append("sizes", JSON.stringify(sizes));
            formData.append("availability", JSON.stringify(availability));
            formData.append("areasOfUse", JSON.stringify(areasOfUse));
            formData.append("siblingStockedSizes", JSON.stringify(siblingStockedSizes));
            formData.append("properties", JSON.stringify(properties));

            // Accordion: convert array → object
            const accordionObj = accordion.reduce((acc, { key, value }) => {
                if (key.trim()) acc[key.trim()] = value;
                return acc;
            }, {});
            formData.append("accordion", JSON.stringify(accordionObj));

            const validColors = colors.filter((c) => c.name?.trim());
            const colorsData = validColors.map((c) => {
                const sortedGrid = [...(c.gridImages || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
                let fileIdx = 0;
                const finalOrder = sortedGrid.map(() => `NEW_FILE_${fileIdx++}`);
                return {
                    name: c.name,
                    image: c.image || "",
                    heroImage: c.heroImage || "",
                    hoverImage: c.hoverImage || "",
                    gridImagesFinalOrder: finalOrder
                };
            });
            formData.append("colors", JSON.stringify(colorsData));

            // Main images
            if (bannerImage[0]?.originFileObj)
                formData.append("bannerImage", bannerImage[0].originFileObj);
            if (bannerImageMobile[0]?.originFileObj)
                formData.append("bannerImageMobile", bannerImageMobile[0].originFileObj);

            // Gallery images — sorted by order before appending
            const sortedGallery = [...galleryImages].sort((a, b) => (a.order || 0) - (b.order || 0));
            sortedGallery.forEach(({ file }) => {
                if (file) formData.append("galleryImages", file);
            });

            // Color images
            validColors.forEach((c, i) => {
                if (c.imageFile) formData.append(`colorImage_${i}`, c.imageFile);
                if (c.heroImageFile) formData.append(`colorHeroImage_${i}`, c.heroImageFile);
                if (c.hoverImageFile) formData.append(`colorHoverImage_${i}`, c.hoverImageFile);
                const sortedGridList = [...(c.gridImages || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
                sortedGridList.forEach((img) => {
                    if (img.file) formData.append(`colorGridImage_${i}`, img.file);
                });
            });

            const response = await http.post(base_url, formData);

            // Upload PDF resources separately if any
            const newResources = resources.filter((f) => f.originFileObj);
            if (newResources.length > 0 && response && (response._id || response.id)) {
                const productId = response._id || response.id;
                const resFormData = new FormData();
                newResources.forEach((f) => resFormData.append("resources", f.originFileObj));
                await http.post(`${base_url}/${productId}/resources`, resFormData).catch(console.error);
            }

            message.success("Product created successfully!");
            const finalCategory = (formValues.category || selectedCategory || "").toString().trim().toLowerCase();
            navigate(`/products${finalCategory ? `?category=${finalCategory}` : ""}`);
        } catch (error) {
            if (error.errorFields) return;
            console.error("Submit error:", error);
            message.error(error?.message || "Failed to create product");
        } finally {
            setSubmitLoading(false);
        }
    };

    // ── Color helpers ───────────────────────────────────────────────────────

    const handleAddColor = () => setColors((p) => [...p, { name: "", image: "", heroImage: "", hoverImage: "", gridImages: [] }]);
    const handleRemoveColor = (i) => setColors((p) => p.filter((_, idx) => idx !== i));
    const handleColorName = (i, val) =>
        setColors((p) => { const u = [...p]; u[i] = { ...u[i], name: val }; return u; });
    const handleColorImage = (i, file) => {
        setColors((p) => { const u = [...p]; u[i] = { ...u[i], imageFile: file }; return u; });
        return false;
    };
    const handleColorHeroImage = (i, file) => {
        setColors((p) => { const u = [...p]; u[i] = { ...u[i], heroImageFile: file }; return u; });
        return false;
    };
    const handleColorHoverImage = (i, file) => {
        setColors((p) => { const u = [...p]; u[i] = { ...u[i], hoverImageFile: file }; return u; });
        return false;
    };
    const handleColorGridImageAdd = (i, file) => {
        setColors((p) => {
            const u = [...p];
            const prevGrid = u[i].gridImages || [];
            if (prevGrid.length >= 2) return u;
            u[i] = { ...u[i], gridImages: [...prevGrid, { uid: file.uid, file, order: prevGrid.length + 1 }] };
            return u;
        });
        return false;
    };

    const handleColorGridImageBatchAdd = (i, fileList) => {
        setColors((p) => {
            const u = [...p];
            const prevGrid = u[i].gridImages || [];
            const remaining = 2 - prevGrid.length;
            if (remaining <= 0) return u;

            const newFiles = fileList.slice(0, remaining).map((file, idx) => ({
                uid: file.uid,
                file,
                order: prevGrid.length + idx + 1
            }));

            u[i] = { ...u[i], gridImages: [...prevGrid, ...newFiles] };
            return u;
        });
        return false;
    };
    const handleColorGridImageRemove = (i, uid) => {
        setColors((p) => {
            const u = [...p];
            u[i] = { ...u[i], gridImages: (u[i].gridImages || []).filter(item => item.uid !== uid) };
            return u;
        });
    };
    const handleColorGridOrder = (i, uid, val) => {
        setColors((p) => {
            const u = [...p];
            u[i] = {
                ...u[i],
                gridImages: (u[i].gridImages || []).map(item => item.uid === uid ? { ...item, order: val } : item)
            };
            return u;
        });
    };

    // ── Property helpers ────────────────────────────────────────────────────

    const handleAddProp = () => {
        if (propertyKey.trim()) {
            setProperties((p) => ({ ...p, [propertyKey.trim()]: propertyValue.trim() }));
            setPropertyKey("");
            setPropertyValue("");
        }
    };
    const handleRemoveProp = (key) =>
        setProperties((p) => { const u = { ...p }; delete u[key]; return u; });

    const uploadButton = (
        <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Upload</div>
        </div>
    );

    // ── Reusable ordered-image list ─────────────────────────────────────────

    const OrderedImageList = ({ items, maxCount, onRemove, onOrderChange }) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {items.map((item) => {
                const previewUrl = item.file ? URL.createObjectURL(item.file) : item.url;
                const isVideo = item.file ? item.file.name.toLowerCase().endsWith('.mp4') : (item.name ? item.name.toLowerCase().endsWith('.mp4') : false);
                return (
                    <div
                        key={item.uid}
                        style={{
                            width: 110,
                            border: "1px solid #d9d9d9",
                            borderRadius: 8,
                            overflow: "hidden",
                            background: "#fafafa",
                        }}
                    >
                        <div style={{ position: "relative" }}>
                            {isVideo ? (
                                <video
                                    src={previewUrl}
                                    style={{ width: "100%", height: 80, objectFit: "cover", display: "block" }}
                                    muted loop autoPlay playsInline
                                />
                            ) : (
                                <img
                                    src={previewUrl}
                                    alt=""
                                    style={{ width: "100%", height: 80, objectFit: "cover", display: "block" }}
                                />
                            )}
                            <Button
                                type="text"
                                danger
                                size="small"
                                icon={<MinusCircleOutlined />}
                                onClick={() => onRemove(item.uid)}
                                style={{
                                    position: "absolute", top: 2, right: 2,
                                    background: "rgba(255,255,255,0.85)", borderRadius: 4,
                                }}
                            />
                        </div>
                        <div style={{ padding: "6px 6px 8px" }}>
                            <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>Sort order</div>
                            <InputNumber
                                min={1}
                                max={maxCount}
                                value={item.order}
                                onChange={(val) => onOrderChange(item.uid, val)}
                                size="small"
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
            <div style={{ marginBottom: 24 }}>
                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate(`/products${selectedCategory ? `?category=${selectedCategory}` : ""}`)}
                    style={{ marginBottom: 16 }}
                >
                    Back to Products
                </Button>
                <h1 style={{ margin: 0 }}>Create Product</h1>
            </div>

            <Form form={form} layout="vertical" initialValues={{ status: "active", featured: false }}>

                {/* ── Basic Information ─────────────────────────────────── */}
                <Card title="Basic Information" style={{ marginBottom: 24 }}>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Product Name"
                                name="name"
                                rules={[{ required: true, message: "Please enter product name" }]}
                            >
                                <Input size="large" placeholder="Enter product name" />
                            </Form.Item>
                        </Col>
                        <Col xs={0} md={0}>
                            <Form.Item name="category" style={{ display: "none" }}>
                                <Input type="hidden" />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item label="Description" name="description">
                                <HtmlEditor
                                    height={300}
                                    value={description}
                                    onEditorChange={(content) => setDescription(content)}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} style={{ display: "none" }}>
                            <Form.Item label="URL Slug (optional)" name="slug" help="Leave blank to auto-generate">
                                <Input size="large" placeholder="e.g., crimson-wool-rug" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* <Card title="Keywords" style={{ marginTop: 20 }}>
                    <Form.Item label="Keywords (SEO)">
                        <Select
                            mode="tags" size="large" style={{ width: "100%" }}
                            placeholder="Add keywords and press Enter"
                            value={keywords} onChange={setKeywords}
                        />
                    </Form.Item>
                </Card> */}

                <Card title="Product Status" style={{ marginTop: 20 }}>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item label="Status" name="status">
                                <Select size="large">
                                    <Select.Option value="active">Active</Select.Option>
                                    <Select.Option value="inactive">Inactive</Select.Option>
                                    <Select.Option value="draft">Draft</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Featured" name="featured" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </Col>
                        {!isBespoke && (
                            <Col xs={24}>
                                <Form.Item label="Linked Product (Available as Carpet/Rug)" name="linkedProduct">
                                    <Select
                                        size="large"
                                        placeholder="Select product to link (opposite category)"
                                        allowClear
                                        showSearch
                                        optionFilterProp="children"
                                    >
                                        {linkedProductsList.map(p => (
                                            <Select.Option key={p._id} value={p._id}>
                                                {p.name} ({p.category})
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}
                    </Row>
                </Card>

                {/* ── Product Details (Always visible) ─────── */}
                {!isBespoke && (
                    <Card title="Product Details" style={{ marginBottom: 24 }}>
                        <Row gutter={16}>
                            <>
                                <Col xs={24}>
                                    <Form.Item label="Tags" name="availability" rules={[{ required: true, message: "Please enter at least one tag" }]}>
                                        <Select mode="tags" size="large" style={{ width: "100%" }}
                                            placeholder="e.g., Designer, Classic, Modern"
                                            value={availability} onChange={setAvailability} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item label="Available Sizes" name="sizes" rules={[{ required: true, message: "Please enter at least one size" }]}>
                                        <Select mode="tags" size="large" style={{ width: "100%" }}
                                            placeholder="e.g., 5x7, 8x10, Custom"
                                            value={sizes} onChange={setSizes} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item label="Related Variant Sizes" name="siblingStockedSizes">
                                        <Select mode="tags" size="large" style={{ width: "100%" }}
                                            placeholder="e.g., 5x7, 8x10, Custom"
                                            value={siblingStockedSizes} onChange={setSiblingStockedSizes} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item label="Areas of Use" name="areasOfUse" rules={[{ required: true, message: "Please enter at least one area of use" }]}>
                                        <Select mode="tags" size="large" style={{ width: "100%" }}
                                            placeholder="e.g., Living Room, Bedroom, Office"
                                            value={areasOfUse} onChange={setAreasOfUse} />
                                    </Form.Item>
                                </Col>
                            </>

                            {/* Properties */}
                            <Col xs={24}>
                                <Form.Item name="properties" rules={[{
                                    validator: (_, value) => {
                                        if (!value || Object.keys(value).length === 0) {
                                            return Promise.reject("At least one product property is required");
                                        }
                                        return Promise.resolve();
                                    }
                                }]}>
                                    <Divider orientation="left">Product Properties</Divider>
                                    <Row gutter={8} style={{ marginBottom: 12 }}>
                                        <Col xs={10}>
                                            <Input placeholder="Name (e.g., Material)" value={propertyKey}
                                                onChange={(e) => setPropertyKey(e.target.value)} onPressEnter={handleAddProp} />
                                        </Col>
                                        <Col xs={10}>
                                            <Input placeholder="Value (e.g., Wool)" value={propertyValue}
                                                onChange={(e) => setPropertyValue(e.target.value)} onPressEnter={handleAddProp} />
                                        </Col>
                                        <Col xs={4}>
                                            <Button type="primary" onClick={handleAddProp} block>Add</Button>
                                        </Col>
                                    </Row>
                                    <div>
                                        {Object.entries(properties).map(([key, val]) => (
                                            <Tag key={key} closable onClose={() => handleRemoveProp(key)}
                                                style={{ marginBottom: 8, padding: "4px 8px" }}>
                                                <strong>{key}:</strong> {val}
                                            </Tag>
                                        ))}
                                    </div>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>
                )}

                {/* ── Colors & Variants (Always visible) ────────────────── */}
                <Card title="Color Variants" style={{ marginBottom: 24 }}>
                    <Form.Item name="colors" rules={[{
                        validator: (_, value) => {
                            if (!value || value.length === 0) return Promise.reject("At least one color variant is required");
                            for (let i = 0; i < value.length; i++) {
                                const c = value[i];
                                if (!c.name?.trim()) return Promise.reject(`Variant ${i + 1}: Name is required`);
                                if (!c.imageFile) return Promise.reject(`Variant ${i + 1}: Swatch is required`);
                                if (!c.heroImageFile) return Promise.reject(`Variant ${i + 1}: Hero is required`);
                                // if (!c.hoverImageFile) return Promise.reject(`Variant ${i + 1}: Hover image is required`);
                                if (!isBespoke && (!c.gridImages || c.gridImages.length < 2)) return Promise.reject(`Variant ${i + 1}: Exactly 2 grid images are required`);
                            }
                            return Promise.resolve();
                        }
                    }]}>
                        <Col xs={24}>
                            <Button type="dashed" onClick={handleAddColor} icon={<PlusOutlined />} block style={{ marginBottom: 16 }}>
                                Add Color Variant
                            </Button>
                            {colors.map((color, i) => (
                                <Card key={i} size="small" style={{ marginBottom: 12 }}
                                    extra={<Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => handleRemoveColor(i)} />}>
                                    <Row gutter={16} align="top">
                                        <Col xs={24} md={10}>
                                            <div style={{ marginBottom: 8, fontWeight: 500 }}>Color Name</div>
                                            <Input placeholder="e.g., Crimson Red"
                                                value={color.name} onChange={(e) => handleColorName(i, e.target.value)}
                                                style={{ marginBottom: 16 }} />

                                            <div style={{ marginBottom: 8, fontWeight: 500 }}>Swatch Image</div>
                                            <Upload listType="picture-card" maxCount={1}
                                                beforeUpload={(file) => handleColorImage(i, file)}
                                                onRemove={() => handleColorImage(i, null)}
                                                showUploadList={!!color.imageFile}>
                                                {!color.imageFile && uploadButton}
                                            </Upload>
                                        </Col>
                                        <Col xs={24} md={14}>
                                            <Row gutter={16}>
                                                <Col span={8}>
                                                    <div style={{ marginBottom: 8, fontWeight: 500 }}>Hero Image</div>
                                                    <Upload listType="picture-card" maxCount={1}
                                                        beforeUpload={(file) => handleColorHeroImage(i, file)}
                                                        onRemove={() => handleColorHeroImage(i, null)}
                                                        showUploadList={!!color.heroImageFile}>
                                                        {!color.heroImageFile && uploadButton}
                                                    </Upload>
                                                </Col>
                                                <Col span={8}>
                                                    <div style={{ marginBottom: 8, fontWeight: 500 }}>Hover Image</div>
                                                    <Upload listType="picture-card" maxCount={1}
                                                        beforeUpload={(file) => handleColorHoverImage(i, file)}
                                                        onRemove={() => handleColorHoverImage(i, null)}
                                                        showUploadList={!!color.hoverImageFile}>
                                                        {!color.hoverImageFile && uploadButton}
                                                    </Upload>
                                                </Col>
                                                {!isBespoke && (
                                                    <Col span={8}>
                                                        <div style={{ marginBottom: 8, fontWeight: 500 }}>Grid (Max 2)</div>
                                                        <Upload
                                                            listType="picture-card"
                                                            showUploadList={false}
                                                            multiple
                                                            beforeUpload={(_, fileList) => handleColorGridImageBatchAdd(i, fileList)}
                                                            disabled={(color.gridImages || []).length >= 2}
                                                        >
                                                            {(color.gridImages || []).length < 2 ? uploadButton : null}
                                                        </Upload>
                                                        {(color.gridImages || []).length > 0 && (
                                                            <div style={{ marginTop: 8 }}>
                                                                <OrderedImageList
                                                                    items={color.gridImages || []}
                                                                    maxCount={2}
                                                                    onRemove={(uid) => handleColorGridImageRemove(i, uid)}
                                                                    onOrderChange={(uid, val) => handleColorGridOrder(i, uid, val)}
                                                                />
                                                            </div>
                                                        )}
                                                    </Col>
                                                )}
                                            </Row>
                                        </Col>
                                    </Row>
                                </Card>
                            ))}
                        </Col>
                    </Form.Item>
                </Card>

                {/* ── Accordion Sections (Always visible) ───────────────── */}
                <Card title="Accordion Sections" style={{ marginBottom: 24 }}>
                    <Col xs={24}>
                        <Row gutter={8} style={{ marginBottom: 12 }}>
                            <Col xs={10}>
                                <Input placeholder="Title (e.g., Care Instructions)"
                                    value={accordionKey} onChange={(e) => setAccordionKey(e.target.value)}
                                    onPressEnter={handleAddAccordion} />
                            </Col>
                            <Col xs={10}>
                                <HtmlEditor
                                    height={150}
                                    value={accordionValue}
                                    onEditorChange={(content) => setAccordionValue(content)}
                                />
                            </Col>
                            <Col xs={4}>
                                <Button type="primary" onClick={handleAddAccordion} block style={{ marginTop: 4 }}>Add</Button>
                            </Col>
                        </Row>
                        {accordion.map((item, idx) => (
                            <Card key={idx} size="small" style={{ marginBottom: 8 }}
                                extra={
                                    <Button type="text" danger size="small"
                                        icon={<MinusCircleOutlined />}
                                        onClick={() => handleRemoveAccordion(idx)} />
                                }>
                                <Row gutter={8}>
                                    <Col xs={24} md={8}>
                                        <Input
                                            size="small"
                                            value={item.key}
                                            onChange={(e) => handleUpdateAccordionKey(idx, e.target.value)}
                                            placeholder="Title"
                                        />
                                    </Col>
                                    <Col xs={24} md={16}>
                                        <HtmlEditor
                                            height={150}
                                            value={item.value}
                                            onEditorChange={(content) => handleUpdateAccordionValue(idx, content)}
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        ))}
                    </Col>
                </Card>

                {/* ── Product Images ────────────────────────────────────── */}
                <Card title="Product Images" style={{ marginBottom: 24 }}>
                    <Row gutter={16}>
                        {/* Banner Images */}
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Banner Image (Desktop)"
                                name="bannerImage"
                                rules={[{ required: true, message: "Desktop banner is required" }]}
                                extra="Wide full-width banner displayed at the top for desktop users"
                            >
                                <Upload
                                    listType="picture-card"
                                    fileList={bannerImage}
                                    onChange={({ fileList }) => setBannerImage(fileList.slice(-1))}
                                    beforeUpload={() => false}
                                >
                                    {bannerImage.length >= 1 ? null : uploadButton}
                                </Upload>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Banner Image (Mobile)"
                                name="bannerImageMobile"
                                rules={[{ required: true, message: "Mobile banner is required" }]}
                                extra="Optimized banner for smaller mobile screens"
                            >
                                <Upload
                                    listType="picture-card"
                                    fileList={bannerImageMobile}
                                    onChange={({ fileList }) => setBannerImageMobile(fileList.slice(-1))}
                                    beforeUpload={() => false}
                                >
                                    {bannerImageMobile.length >= 1 ? null : uploadButton}
                                </Upload>
                            </Form.Item>
                        </Col>
                        <Col xs={24}><Divider /></Col>

                        {/* Gallery Images with sort order */}
                        <Col xs={24}>
                            <Form.Item
                                label="Gallery Images (Max 12)"
                                extra="Set the sort order number to control display position"
                            >
                                <Upload
                                    listType="picture-card"
                                    showUploadList={false}
                                    beforeUpload={handleGalleryImageAdd}
                                    disabled={galleryImages.length >= 12}
                                    style={{ marginBottom: 12 }}
                                >
                                    {galleryImages.length < 12 ? uploadButton : null}
                                </Upload>
                                {galleryImages.length > 0 && (
                                    <OrderedImageList
                                        items={galleryImages}
                                        maxCount={12}
                                        onRemove={handleGalleryImageRemove}
                                        onOrderChange={handleGalleryOrder}
                                    />
                                )}
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>
                {/* ── Catalog Document ── */}
                {!isBespoke && (
                    <Card title="Catalog Document" style={{ marginBottom: 24 }}>
                        <Row gutter={16}>
                            <Col xs={24}>
                                <Form.Item label="Catalog Document" required={!isBespoke}
                                    help={!isBespoke && resources.length === 0 ? "Catalog PDF is required" : null}
                                    validateStatus={!isBespoke && resources.length === 0 ? "error" : ""}
                                >
                                    <Upload
                                        fileList={resources}
                                        onChange={({ fileList }) => setResources(fileList.slice(-1))}
                                        beforeUpload={() => false}
                                        accept=".pdf"
                                        multiple={false}
                                        maxCount={1}
                                    >
                                        {resources.length === 0 && (
                                            <Button icon={<UploadOutlined />}>Upload</Button>
                                        )}
                                    </Upload>

                                    {resources.length > 0 && (
                                        <div style={{ fontSize: 12, color: "#888", marginTop: 10 }}>
                                            Replace existing PDF by uploading a new one
                                        </div>
                                    )}
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>
                )}

                <div style={{ marginTop: 24 }}>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSubmit}
                        loading={submitLoading} size="large" style={{ marginRight: 8 }}>
                        Create Product
                    </Button>
                    <Button onClick={() => navigate(`/products${selectedCategory ? `?category=${selectedCategory.toLowerCase()}` : ""}`)}>
                        Cancel
                    </Button>
                </div>
            </Form>
        </div>
    );
}

export default ProductCreate;